import { inject, Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";

import { Observable, Subject, map, of, shareReplay, switchMap, tap } from "rxjs";

import { Race } from "../interfaces/race";

@Injectable({
  providedIn: 'root'
})
export class RaceService {
  http = inject(HttpClient);

  private key_racesForCampaignId (campaignId: string): string {
    return 'racesForCampaign' + campaignId;
  }

  private key_racesForUserId (userId: string): string {
    return 'racesForUser' + userId;
  }

  private updateCampaignCache (race: Race) {
    let key = this.key_racesForCampaignId(race.campaignId);
    let cachedRaceIds = this.getRaceIdsForKey(key);
    if (cachedRaceIds && cachedRaceIds.indexOf(race._id) === -1) {
      let raceIds = JSON.parse(cachedRaceIds) as string[];
      raceIds.push(race._id);
      localStorage.setItem(key, JSON.stringify(raceIds));
    }
  };

  private processRaces (races: Race[], key: string = undefined) {
    if (key) {
      let raceIds = races.map(race => race._id);
      localStorage.setItem(key, JSON.stringify(raceIds));
    }
    for (const race of races) {
      if (!key) {
        this.updateCampaignCache(race);
      }
      localStorage.setItem(race._id, JSON.stringify(race));
    }
  };

  private getRaceForId (id: string): Race | undefined {
    let raceObject = localStorage.getItem(id);
    let race = undefined;
    if (raceObject) {
      race = JSON.parse(raceObject) as Race;
    }
    return race;
  };

  getRaceForId$ (id: string): Observable<Race | undefined> {
    let race = this.getRaceForId(id);
    if (race) {
      return of(race);
    }
    return this.http.get<Race>('/api/race/id/' + id).pipe(
      tap(race => this.processRaces([race])),
      shareReplay(1)
    );
  }

  private getRacesForRaceIds (cachedRaceIds: string): Race[] | undefined {
    let raceIds = JSON.parse(cachedRaceIds) as string[];
    let races = undefined;
    if (raceIds) {
      races = raceIds.map(id => this.getRaceForId(id) as Race);
    }
    return races;
  };

  private getRacesForFetchAndKey$ (fetch: Observable<Race[]>, key: string = undefined): Observable<Race[]> {
    return fetch.pipe(
      tap(races => {
        this.processRaces(races, key);
        return races;
      })
    );
  }

  private getRaceIdsForKey (key: string): string | undefined {
    return localStorage.getItem(key);
  };

  getRacesForUserId (userId: string) {
    if (!userId) {
      throw new Error("userId can not be undefined to get user's races");
    }
    let key = this.key_racesForUserId(userId);
    let cachedRaceIds = this.getRaceIdsForKey(key);
    if (cachedRaceIds) {
      let races = this.getRacesForRaceIds(cachedRaceIds);
      return of<Race[]>(races as Race[]);
    }
    return this.getRacesForFetchAndKey$(this.http.get<Race[]>('/api/race/user/' + userId), key);
  };

  getRacesForCampaignId (campaignId: string): Observable<Race[]> {
    if (campaignId == null) {
      throw new Error("campaignId can not be undefined to load all races");
    }

    let key = this.key_racesForCampaignId(campaignId);
    let cachedRaceIds = this.getRaceIdsForKey(key);
    if (cachedRaceIds) {
      let races = this.getRacesForRaceIds(cachedRaceIds);
      return of<Race[]>(races as Race[]);
    }
    return this.getRacesForFetchAndKey$(this.http.get<Race[]>('/api/race/campaign/' + campaignId), key);
  };

  getRacesForStarSystemId (starSystemId: string) {
    // consider creating a cache by starSystemId
    return this.getRacesForFetchAndKey$(this.http.get<Race[]>('/api/race/starSystemId/' + starSystemId));
  };

  deleteRace (race: Race) {
    // clear the cache
    localStorage.removeItem(race._id);
    // clear vs remove single race for the campaign and each player cache, if it exists
    let key = this.key_racesForCampaignId(race.campaignId);
    localStorage.removeItem(key);
    for (const playerId of race.playerIds) {
      let key = this.key_racesForUserId(playerId);
      localStorage.removeItem(key);
    }
    return this.http.delete('/api/race/' + race._id);
  };

  saveRace (race: Race) {
    return this.http.put<Race>('/api/race/' + race['_id'], race).pipe(
      tap(race => this.processRaces([race]))
    );
  };
}
